import React, { useCallback, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import EmailIcon from '@atlaskit/icon/glyph/email';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { router } from '@forge/bridge';

import { ImportResult } from '../../../../../../src/services/import-export/model';
import { FILE_TYPE, validateInput } from '../../../../../../src/services/import-export/parseAndValidate';
import { ImportExportLevel } from '../../../../../../src/services/import-export/public_model';
import { OptionItemObject } from '../../../../../../src/shared_models/interfaces';
import UploadFileImage from '../../../assets/UploadFileImage';
import { DOCS_URL } from '../../../common/constants';

import { Description, DragDropSection, FooterContent, Support } from './indexStyled';
import UploadSuccessMessage from './UploadSuccessMessage';
import UploadWarningMessage from './UploadWarningMessage';

type Props = {
    onClose: () => void;
    onImportClick: (file: ImportExportLevel) => void;
    options: OptionItemObject[];
};

const CUSTOMER_PORTAL_URL = 'https://support.appsvio.com';

const ImportConfigurationModal: React.FC<Props> = ({ onClose, onImportClick, options }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isFileValid, setIsFileValid] = useState(false);
    const [isFileUploaded, setIsFileUploaded] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [importResult, setImportResult] = useState<ImportResult | undefined>(undefined);

    const readFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const content = reader.result as string;
            parseFileContent(file, content);
        };
        reader.onerror = () => console.error('Error reading file:', reader.error);
        reader.readAsText(file);
    };

    const parseFileContent = (file: File, content: string) => {
        const result = validateInput(file.type.includes('json') ? FILE_TYPE.JSON : FILE_TYPE.CSV, content, options);
        console.log('result', result);
        setImportResult(result);
        setIsFileValid(result.success);
    };

    const handleImport = () => {
        if (importResult) {
            setIsSaving(true);
            onImportClick(importResult.data);
        }
    };

    const handleUploadedFile = (file: File) => {
        if (['application/json', 'text/csv'].includes(file.type)) {
            setFile(file);
            setIsFileUploaded(true);
            readFile(file);
        }
    };

    const onFileDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            const file = event.dataTransfer.files[0];
            handleUploadedFile(file);
        }
    }, []);

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            handleUploadedFile(file);
        }
    };

    const openFileDialog = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.json,.csv';
        fileInput.onchange = onFileChange;
        fileInput.click();
    };

    const renderUploadSection = (showDescription = true) => (
        <>
            {showDescription && (
                <Description>
                    During the import you can add new options or update existing ones.
                    <a onClick={() => router.open(DOCS_URL)}> Learn more</a>
                </Description>
            )}
            <DragDropSection
                onDrop={onFileDrop}
                onDragOver={e => e.preventDefault()}
            >
                <UploadFileImage />
                <div className='text'>Drag & drop your file here or</div>
                <Button onClick={openFileDialog}>Upload a CSV or JSON file</Button>
            </DragDropSection>
        </>
    );

    const renderMessageSection = () =>
        importResult?.success ?
            <UploadSuccessMessage
                fileName={file?.name}
                optionsAdded={importResult?.statistics?.toAdd}
                optionsUpdated={importResult?.statistics?.toUpdate}
            />
        :   <>
                <UploadWarningMessage
                    errors={importResult?.errors || []}
                    fileName={file?.name}
                />
                {renderUploadSection(false)}
            </>;

    return (
        <Modal
            onClose={() => !isSaving && onClose()}
            autoFocus={false}
            width='medium'
        >
            <ModalHeader>
                <ModalTitle>Import options</ModalTitle>
            </ModalHeader>
            <ModalBody>{isFileUploaded ? renderMessageSection() : renderUploadSection()}</ModalBody>
            <ModalFooter>
                <FooterContent>
                    <Support onClick={() => router.open(CUSTOMER_PORTAL_URL)}>
                        <EmailIcon
                            size='medium'
                            label='contact support'
                            testId='contact-support-icon'
                        />
                        Contact support
                    </Support>
                    <ButtonGroup>
                        <LoadingButton
                            onClick={() => onClose()}
                            appearance='subtle'
                            isLoading={isSaving}
                            testId='cancel-btn'
                        >
                            Cancel
                        </LoadingButton>
                        <LoadingButton
                            onClick={handleImport}
                            appearance='primary'
                            isLoading={isSaving}
                            isDisabled={!isFileValid}
                        >
                            Import
                        </LoadingButton>
                    </ButtonGroup>
                </FooterContent>
            </ModalFooter>
        </Modal>
    );
};

export default ImportConfigurationModal;
